'use client'

import { useEffect } from 'react'

/**
 * iOS Safari colors its status bar (top) and toolbar (bottom) based on:
 *   - Top: <meta name="theme-color">
 *   - Bottom: document.body.style.backgroundColor
 *
 * When a drawer or modal overlay opens, both need to change to match
 * the overlay's semi-transparent background, otherwise they stay white
 * while the rest of the screen shows the darkened overlay.
 *
 * We use useEffect so the colors update before
 * the browser paints — avoiding a single-frame flash of the wrong color.
 *
 * The blend() function simulates alpha compositing: it computes what
 * the overlay color (rgba(3, 7, 18, 0.4)) looks like on top of the
 * current page background. This must match the CSS overlay defined in
 * mtag-theme/src/index.css.
 */

// Must match the overlay in mtag-theme/src/index.css: rgba(3, 7, 18, 0.4)
const OVERLAY = { r: 3, g: 7, b: 18, a: 0.4 }

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

  if (!result) {
    return { r: 255, g: 255, b: 255 }
  }

  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  }
}

// Alpha compositing: result = bg * (1 - alpha) + overlay * alpha
function blend(bgHex: string) {
  const bg = hexToRgb(bgHex)

  const r = Math.round(bg.r * (1 - OVERLAY.a) + OVERLAY.r * OVERLAY.a)
  const g = Math.round(bg.g * (1 - OVERLAY.a) + OVERLAY.g * OVERLAY.a)
  const b = Math.round(bg.b * (1 - OVERLAY.a) + OVERLAY.b * OVERLAY.a)

  return `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

export function useOverlayThemeColor(isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const metaTag = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    )

    // Save originals so we can restore when the overlay closes
    const originalThemeColor = metaTag?.content || ''
    const originalBodyBg = document.body.style.backgroundColor
    const blended = blend(originalThemeColor || '#FFFFFF')

    if (metaTag) {
      metaTag.content = blended
    }

    document.body.style.backgroundColor = blended

    return () => {
      if (metaTag) {
        metaTag.content = originalThemeColor
      }

      document.body.style.backgroundColor = originalBodyBg
    }
  }, [isOpen])
}
